import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { generateEnhancedAudioGuide, generateChapterAudio } from '@/lib/api';
import { AudioPlayer } from '@/components/AudioPlayer';
import { toast } from 'sonner';

/**
 * Global Audio Player Context — Chaptered Audio Guide
 * Manages chapter-by-chapter playback with preloading.
 * Player persists independently of the component that triggered it.
 */
const AudioPlayerContext = createContext({
  isOpen: false,
  isGenerating: false,
  generationMessage: '',
  chapters: [],
  currentChapterIndex: 0,
  loadingChapterIndex: null,
  title: '',
  startAudioGuide: async () => {},
  goToChapter: () => {},
  closePlayer: () => {},
});

export const useAudioPlayer = () => useContext(AudioPlayerContext);

export const AudioPlayerProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [title, setTitle] = useState('');
  const [chapters, setChapters] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [loadingChapterIndex, setLoadingChapterIndex] = useState(null);
  const [voiceStyle, setVoiceStyle] = useState(null);

  // Refs to avoid stale closures
  const chaptersRef = useRef([]);
  const langRef = useRef('fr');
  const voiceStyleRef = useRef(null);

  // Load audio for a specific chapter index
  const loadChapterAudio = useCallback(async (index, chaptersArr, vStyle, lang) => {
    const arr = chaptersArr || chaptersRef.current;
    if (!arr[index] || arr[index].audio_url) return;

    setLoadingChapterIndex(index);
    try {
      const response = await generateChapterAudio(index + 1, {
        text: arr[index].text,
        lang: lang || langRef.current,
        voice_style: vStyle || voiceStyleRef.current,
      });

      if (response.data?.audio_url) {
        let audioUrl = response.data.audio_url;
        if (!audioUrl.startsWith('http')) {
          audioUrl = `${process.env.REACT_APP_BACKEND_URL}${audioUrl}`;
        }
        setChapters(prev => {
          const updated = [...prev];
          updated[index] = { ...updated[index], audio_url: audioUrl };
          chaptersRef.current = updated;
          return updated;
        });
      }
    } catch (e) {
      console.error('[AudioPlayer] Chapter load failed:', e);
    } finally {
      setLoadingChapterIndex(null);
    }
  }, []);

  // Start audio guide from scratch — calls /audio/enhanced-guide
  const startAudioGuide = useCallback(async (place, { version = 'medium', lang = 'fr' } = {}) => {
    setIsGenerating(true);
    setIsOpen(false);
    setChapters([]);
    chaptersRef.current = [];
    setCurrentChapterIndex(0);
    setTitle(place.name || '');
    langRef.current = lang;
    setGenerationMessage(lang === 'fr' ? 'Création du guide...' : 'Creating guide...');

    try {
      const response = await generateEnhancedAudioGuide({
        name: place.name,
        category: place.category || '',
        description: place.description || '',
        version,
        lang,
      });

      const { chapters: newChapters, voice_style: vStyle } = response.data;

      // Fix audio URL for first chapter (might be relative)
      const processedChapters = newChapters.map((ch, i) => {
        if (i === 0 && ch.audio_url && !ch.audio_url.startsWith('http')) {
          return { ...ch, audio_url: `${process.env.REACT_APP_BACKEND_URL}${ch.audio_url}` };
        }
        return ch;
      });

      chaptersRef.current = processedChapters;
      voiceStyleRef.current = vStyle;
      setChapters(processedChapters);
      setVoiceStyle(vStyle);
      setCurrentChapterIndex(0);
      setIsGenerating(false);
      setIsOpen(true);

      // Preload chapter 2 in background
      if (processedChapters.length > 1 && !processedChapters[1]?.audio_url) {
        loadChapterAudio(1, processedChapters, vStyle, lang);
      }

      toast.success(lang === 'fr' ? 'Guide audio prêt !' : 'Audio guide ready!');
    } catch (e) {
      console.error('[AudioPlayer] Generation failed:', e);
      setIsGenerating(false);
      toast.error(lang === 'fr' ? 'Erreur de génération' : 'Generation error');
    }
  }, [loadChapterAudio]);

  // Navigate to a chapter
  const goToChapter = useCallback((index) => {
    const arr = chaptersRef.current;
    if (index < 0 || index >= arr.length) return;

    setCurrentChapterIndex(index);

    // If chapter has no audio yet, load it
    if (!arr[index]?.audio_url) {
      loadChapterAudio(index, arr, voiceStyleRef.current, langRef.current);
    }

    // Preload the chapter after
    if (index + 1 < arr.length && !arr[index + 1]?.audio_url) {
      loadChapterAudio(index + 1, arr, voiceStyleRef.current, langRef.current);
    }
  }, [loadChapterAudio]);

  const closePlayer = useCallback(() => {
    setIsOpen(false);
    setChapters([]);
    chaptersRef.current = [];
    setCurrentChapterIndex(0);
  }, []);

  return (
    <AudioPlayerContext.Provider value={{
      isOpen,
      isGenerating,
      generationMessage,
      chapters,
      currentChapterIndex,
      loadingChapterIndex,
      title,
      startAudioGuide,
      goToChapter,
      closePlayer,
    }}>
      {children}

      {/* Global Floating Player */}
      {isOpen && chapters.length > 0 && (
        <AudioPlayer
          chapters={chapters}
          currentChapterIndex={currentChapterIndex}
          loadingChapterIndex={loadingChapterIndex}
          title={title}
          onChapterChange={goToChapter}
          onClose={closePlayer}
        />
      )}

      {/* Generation Loading Indicator */}
      {isGenerating && (
        <div className="fixed bottom-20 right-4 z-50 bg-white rounded-2xl shadow-xl border border-purple-100 p-4 w-72">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">Guide audio en cours</p>
              <p className="text-xs text-gray-500 truncate">{generationMessage}</p>
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full w-3/4 bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse rounded-full" />
          </div>
        </div>
      )}
    </AudioPlayerContext.Provider>
  );
};
